import { QueryButtonTag } from "components/Button/QueryButtonTag/QueryButtonTag";
import { MixpanelActionSource } from "helpers/services/mixpanel/events";
import useLabels from "hooks/useLabels";
import useSearchMode from "hooks/useSearchMode";
import useThread from "hooks/useThread";
import React from "react";
import { SearchParams } from "../hooks/useConsensusMeter";
import { useGeneralizedYesNoQuery } from "../hooks/useGeneralizedYesNoQuery";
import { SubtitleText } from "./SubtitleText";

type FailedMeterContentProps = {
  /**
   * Search id params for threaded or non-threaded search
   */
  searchParams: SearchParams;
  /**
   * The thread ID for the current thread  if in threaded search
   */
  threadId: string;
};

/**
 * Component displayed when the consensus meter cannot be shown
 * due to insufficient data or other errors
 */
export const FailedMeterContent = ({
  searchParams,
  threadId,
}: FailedMeterContentProps) => {
  const { isQueryLoading, generalizedQuery, isSuccess } =
    useGeneralizedYesNoQuery(searchParams);
  const [errorDesc] = useLabels("consensus-meter.not-enough-paper-error");

  const showQueryButton = isQueryLoading || (isSuccess && !!generalizedQuery);

  const { searchId } = searchParams;

  return (
    <div>
      <SubtitleText content={errorDesc} />
      {showQueryButton && searchId !== undefined && (
        <ThreadedQueryButton
          threadId={threadId}
          generalizedQuery={generalizedQuery}
          isLoading={isQueryLoading}
        />
      )}
    </div>
  );
};

// Sub-components to handle difference between threaded and non-threaded searches

const BaseButtonWrapper = ({ children }: { children: React.ReactNode }) => {
  return (
    <div className="mt-6" data-testid="failed-meter-content-query-button">
      {children}
    </div>
  );
};

interface ThreadedQueryButtonProps {
  threadId: string;
  generalizedQuery?: string;
  isLoading: boolean;
}

const ThreadedQueryButton = ({
  threadId,
  generalizedQuery,
  isLoading,
}: ThreadedQueryButtonProps) => {
  const { addInteractionToThread } = useThread(threadId);
  const { currentSearchMode } = useSearchMode();

  return (
    <BaseButtonWrapper>
      <QueryButtonTag
        q={generalizedQuery}
        isYesNoQuery
        icon="follow-up"
        loading={isLoading}
        onClick={() => {
          if (generalizedQuery) {
            addInteractionToThread({
              userMessage: generalizedQuery,
              filters: {}, // Disable filters on follow up
              searchMode: currentSearchMode,
              actionSource: MixpanelActionSource.ConsensusMeter,
            });
          }
        }}
      />
    </BaseButtonWrapper>
  );
};
